import type { ArchiveStorage } from "@/features/archive/types";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { useComponentNode } from "@/shared/ui/component-hierarchy/hooks";

const BYTES_PER_GIGABYTE: number = 1024 ** 3;
const PERCENTAGE: number = 100;

type ArchiveStorageGaugeProps = {
  readonly boardName?: string;
  readonly hasError?: boolean;
  readonly storage?: ArchiveStorage;
};

export const ArchiveStorageGauge: React.FunctionComponent<
  ArchiveStorageGaugeProps
> = (props: ArchiveStorageGaugeProps) => {
  const node = useComponentNode();

  return (
    <ComponentScope node={node}>
      <ArchiveStorageGaugeView {...props} />
    </ComponentScope>
  );
};

/** 容量表示をpropsだけから描画するView。 */
export const ArchiveStorageGaugeView: React.FunctionComponent<
  ArchiveStorageGaugeProps
> = ({ boardName, hasError = false, storage }: ArchiveStorageGaugeProps) => {
  const usedBytes = Math.max(0, storage?.usedBytes ?? 0);
  const limitBytes = Math.max(0, storage?.limitBytes ?? 0);
  const remainingBytes = Math.max(0, storage?.remainingBytes ?? 0);
  const usageRatio =
    limitBytes > 0 ? Math.min(1, Math.max(0, usedBytes / limitBytes)) : 0;
  const usagePercentage = usageRatio * PERCENTAGE;
  const heading = boardName ? `${boardName}の過去ログ容量` : "過去ログ容量";

  return (
    <section
      aria-label={heading}
      aria-busy={storage === undefined && !hasError}
      className="flex flex-col gap-2 m-2 p-4 rounded-lg bg-card"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-foreground">{heading}</h2>
        <p className="text-xs text-muted-foreground">
          {storage
            ? `使用量 ${(usedBytes / BYTES_PER_GIGABYTE).toFixed(1)}GB / ${(
                limitBytes / BYTES_PER_GIGABYTE
              ).toFixed(1)}GB（残り ${(
                remainingBytes / BYTES_PER_GIGABYTE
              ).toFixed(1)}GB）`
            : hasError
              ? "容量を取得できませんでした"
              : "容量を読み込んでいます"}
        </p>
      </header>
      {storage && (
        <meter
          min={0}
          max={PERCENTAGE}
          value={Number(usagePercentage.toFixed(1))}
          aria-label={`${heading}の使用率`}
          className="archive-storage-meter"
        >
          {usagePercentage.toFixed(1)}%
        </meter>
      )}
    </section>
  );
};
